import Cohere from 'cohere-js';
import React, { useCallback, useEffect, useState } from 'react';

import { UserStatus } from '../graphql/gen/types';
import { useGetLoggedInUserQuery } from '../graphql/queries/GetLoggedInUser';
import { ConfirmActionParams, PlexContext } from '../providers/plex_provider';
import { GenericErrorPage } from './logged_in/GenericErrorPage';
import { LoggedInMain } from './logged_in/LoggedInMain';
import { Error } from './logged_in/shared/Error';
import { SignInMain } from './logged_out/SignInMain';
import { ConfirmationDialog } from './shared/ConfirmationDialog';
import { useSetupAnalytics } from './useSetupAnalytics';

export function Main(): React.ReactElement {
  const [confirmationDialog, setConfirmationDialog] = useState<null | (ConfirmActionParams & { open: boolean })>(null);
  const closeDialog = useCallback(
    (dialogData: ConfirmActionParams & { open: boolean }): void => {
      // instead of removing the dialog, set open to false so we do the nice closing animation on dialog
      setConfirmationDialog((prevState) => {
        if (!!prevState) {
          return {
            ...prevState,
            open: false,
          };
        } else {
          return null;
        }
      });
    },
    [setConfirmationDialog]
  );

  const analyticsDoneLoading = useSetupAnalytics();
  const { data, loading, error } = useGetLoggedInUserQuery({ variables: {} });

  useEffect(() => {
    const user = data?.currentUser;
    if (user) {
      Cohere.identify(user.id, { displayName: user.name, email: user.email });
    }
  }, [data]);

  let content: React.ReactElement;
  if (error) {
    content = <Error error={error} />;
  } else if (loading || !analyticsDoneLoading) {
    content = <div>Loading...</div>;
  } else if (data?.currentUser?.status === UserStatus.Disabled) {
    content = (
      <GenericErrorPage message="Your plex account has been disabled. Please contact a domain admininstrator to remedy this" />
    );
  } else if (data?.currentUser != null && data.currentUser?.domain != null) {
    content = <LoggedInMain userProfile={data?.currentUser} />;
  } else {
    content = <SignInMain />;
  }

  return (
    <PlexContext.Provider
      value={{
        confirmAction: (args: ConfirmActionParams): void => {
          setConfirmationDialog({ ...args, open: true });
        },
      }}
    >
      {content}
      {!!confirmationDialog && <ConfirmationDialog dialogData={confirmationDialog} onClose={closeDialog} />}
    </PlexContext.Provider>
  );
}
